import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import stockStatsService from '../services/stockStatsService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const StockStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await stockStatsService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('fr-FR').format(value || 0);
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  const {
    salesHope,
    general,
    byLocation,
    byStatus,
    outOfStockProducts,
    outOfStockVariants,
    highStockProducts,
    topProductsByValue,
    topVariantsByValue,
    byProduct,
    expiringUnitsDetails,
    stockEvolution,
  } = stats;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        📦 Statistiques de Stock
      </Typography>

      {/* Statistiques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Colonne Valeur totale + Espoir de vente */}
        <Grid item xs={12} md={4.8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, height: '100%' }}>
            <Card sx={{ flex: '0 0 auto', bgcolor: 'primary.dark' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Valeur Totale (avec supplément)
                    </Typography>
                    <Typography variant="h4">{formatCurrency(general.stockValueWithAdditional)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatNumber(general.quantityInStock)} unités
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ flex: '0 0 auto', bgcolor: 'info.dark' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Valeur Totale (sans supplément)
                    </Typography>
                    <Typography variant="h4">{formatCurrency(general.stockValueWithoutAdditional)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Différence: {formatCurrency(general.stockValueWithAdditional - general.stockValueWithoutAdditional)}
                    </Typography>
                  </Box>
                  <MoneyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
            <Paper sx={{ p: 2, textAlign: 'center', flex: '0 0 auto' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <StorefrontIcon fontSize="small" />
                Espoir de vente
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                CA potentiel si tout le stock vendu au prix de vente (déclinaisons incluses)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary" display="block">HT</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(salesHope?.potentialHt ?? 0)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary" display="block">TTC</Typography>
                  <Typography variant="h6" fontWeight="bold">{formatCurrency(salesHope?.potentialTtc ?? 0)}</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', maxHeight: 140, bgcolor: 'success.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Unités en Stock
                  </Typography>
                  <Typography variant="h4">{formatNumber(general.unitsInStock)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formatNumber(general.quantityInStock)} quantités
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', maxHeight: 140, bgcolor: 'warning.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Unités Perdues
                  </Typography>
                  <Typography variant="h4">{formatNumber(general.unitsLost)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formatNumber(general.quantityLost)} quantités
                  </Typography>
                </Box>
                <CancelIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', maxHeight: 140, bgcolor: 'error.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Expirant Bientôt
                  </Typography>
                  <Typography variant="h4">{formatNumber(general.expiringSoonUnits)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formatNumber(general.expiredUnits)} expirées
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Répartition par statut */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Statut
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="unitCount"
                  >
                    {byStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatNumber(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Répartition par emplacement */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Emplacement
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byLocation.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="location" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend />
                  <Bar dataKey="totalQuantity" fill="#8884d8" name="Quantité" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Évolution du stock */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Évolution du Stock (12 derniers mois)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stockEvolution.reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Units créées') {
                        return formatNumber(value);
                      }
                      return formatNumber(value);
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="unitsCreated" stroke="#8884d8" name="Unités créées" />
                  <Line type="monotone" dataKey="quantityAdded" stroke="#82ca9d" name="Quantité ajoutée" />
                  <Line type="monotone" dataKey="quantityInStock" stroke="#ffc658" name="Quantité en stock" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tableaux */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top produits par valeur */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Produits par Valeur de Stock
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Valeur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProductsByValue.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>{product.productName}</TableCell>
                        <TableCell align="right">{formatNumber(product.totalQuantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top variantes par valeur */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Variantes par Valeur de Stock
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit / Variante</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Valeur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topVariantsByValue.map((variant) => (
                      <TableRow key={variant.variantId}>
                        <TableCell>
                          {variant.productName}
                          {variant.variantName && ` - ${variant.variantName}`}
                        </TableCell>
                        <TableCell align="right">{formatNumber(variant.totalQuantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(variant.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Produits en rupture */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error">
                ⚠️ Produits en Rupture de Stock
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell align="right">Minimum</TableCell>
                      <TableCell align="right">Déficit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outOfStockProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="success.main">
                            Aucun produit en rupture
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      outOfStockProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell align="right">{formatNumber(product.stock)}</TableCell>
                          <TableCell align="right">{formatNumber(product.stockMin)}</TableCell>
                          <TableCell align="right">
                            <Chip label={`-${formatNumber(product.deficit)}`} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Variantes en rupture */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error">
                ⚠️ Variantes en Rupture de Stock
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit / Variante</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell align="right">Minimum</TableCell>
                      <TableCell align="right">Déficit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outOfStockVariants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="success.main">
                            Aucune variante en rupture
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      outOfStockVariants.map((variant) => (
                        <TableRow key={variant.id}>
                          <TableCell>
                            {variant.productName}
                            {variant.variantName && ` - ${variant.variantName}`}
                          </TableCell>
                          <TableCell align="right">{formatNumber(variant.stock)}</TableCell>
                          <TableCell align="right">{formatNumber(variant.stockMin)}</TableCell>
                          <TableCell align="right">
                            <Chip label={`-${formatNumber(variant.deficit)}`} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Produits avec stock élevé */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="warning.main">
                📈 Produits avec Stock Élevé
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Stock</TableCell>
                      <TableCell align="right">Maximum</TableCell>
                      <TableCell align="right">Excédent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {highStockProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Aucun produit avec stock élevé
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      highStockProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>{product.name}</TableCell>
                          <TableCell align="right">{formatNumber(product.stock)}</TableCell>
                          <TableCell align="right">{formatNumber(product.stockMax)}</TableCell>
                          <TableCell align="right">
                            <Chip label={`+${formatNumber(product.excess)}`} color="warning" size="small" />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Unités expirant bientôt */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="warning.main">
                ⏰ Unités Expirant Bientôt (30 jours)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Unité</TableCell>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell>Expiration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringUnitsDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="success.main">
                            Aucune unité n'expire bientôt
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      expiringUnitsDetails.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell>{unit.name}</TableCell>
                          <TableCell>
                            {unit.productName}
                            {unit.variantName && ` - ${unit.variantName}`}
                          </TableCell>
                          <TableCell align="right">{formatNumber(unit.quantity)}</TableCell>
                          <TableCell>
                            {unit.expirationDate
                              ? new Date(unit.expirationDate).toLocaleDateString('fr-FR')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Détails supplémentaires */}
      <Grid container spacing={3}>
        {/* Statistiques par emplacement */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques par Emplacement
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Emplacement</TableCell>
                      <TableCell align="right">Unités</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Valeur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byLocation.map((location) => (
                      <TableRow key={location.location}>
                        <TableCell>{location.location}</TableCell>
                        <TableCell align="right">{formatNumber(location.unitCount)}</TableCell>
                        <TableCell align="right">{formatNumber(location.totalQuantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(location.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistiques par produit */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques par Produit (Top 30)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Unités</TableCell>
                      <TableCell align="right">En Stock</TableCell>
                      <TableCell align="right">Perdues</TableCell>
                      <TableCell align="right">Valeur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byProduct.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>{product.productName}</TableCell>
                        <TableCell align="right">{formatNumber(product.unitCount)}</TableCell>
                        <TableCell align="right">{formatNumber(product.quantityInStock)}</TableCell>
                        <TableCell align="right">{formatNumber(product.quantityLost)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.valueInStock)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Résumé financier */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Résumé Financier du Stock
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Valeur totale du stock (avec supplément)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(general.stockValueWithAdditional)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Valeur totale du stock (sans supplément)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="info.main">
                      {formatCurrency(general.stockValueWithoutAdditional)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Prix d'achat moyen
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(general.avgBuyPrice)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Prix d'achat (min - max)
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(general.minBuyPrice)} - {formatCurrency(general.maxBuyPrice)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockStats;
